import { z } from 'zod';
import { BATTLE_ACTIONS, BATTLE_OUTCOMES, BATTLE_SIDES, CPU_OPPONENT_IDS, CPU_TIERS, } from '../constants/index.js';
import { archetypeIdSchema, characterStatsSchema, elementIdSchema, traitIdSchema, } from './character.js';
export const battleSideSchema = z.enum(BATTLE_SIDES);
export const battleOutcomeSchema = z.enum(BATTLE_OUTCOMES);
export const battleActionSchema = z.enum(BATTLE_ACTIONS);
/**
 * 戦闘に参加するキャラクターのスナップショット。
 *
 * `Character` への参照ではなく値のコピーにしてあるのは、**戦闘ログが後から再生される**
 * ため（企画書 §10.3, §18）。キャラクターは戦績が更新され、将来的には育成や覚醒で
 * ステータスも変わりうる。参照にすると、去年の試合を再生したときに今のステータスで
 * 描画されてしまう。
 */
export const battleCharacterSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    spriteImageUrl: z.string().url().optional(),
    archetypeId: archetypeIdSchema,
    /** 相性でダメージが変わる（ADR-027）。無い（鑑定より前の古い選手）ときは相性なし。 */
    elementId: elementIdSchema.optional(),
    traitIds: z.array(traitIdSchema),
    /** プレイヤーが配分した能力値。 */
    stats: characterStatsSchema,
    /**
     * 戦闘開始時の実 HP。
     *
     * `stats.hp` は配分ポイント（企画書 §8.1 の例では 20）であって実 HP ではない。実 HP は
     * エンジンが `stats.hp` から導出する（§10.4 の例では残 HP が 62 になっている）。
     * 演出側が HP ゲージを描くには分母が要るので、導出結果をログに含める。
     *
     * 導出式は `@drawgon/game-engine` が持ち、数値は `docs/specs/battle-engine.md` にある。
     */
    maxHp: z.number().int().positive(),
    /**
     * 必殺技の名前（#204、ADR-028）。**演出専用。エンジンは読まない**（企画書 §40-1）。
     *
     * 無いのは、名前の無い選手と、この変更より前のログ。**上限（`max`）は付けない**（`name` と同じ。上限を
     * あとで縮めても、保存済みのログを `GET /battles/:id` で弾かないため）。
     */
    specialMoveName: z.string().min(1).optional(),
});
/**
 * 1 回の行動の記録（企画書 §10.4）。
 *
 * `turn` はラウンド番号で、配列のインデックスとは一致しない。系統「速」の追加行動
 * （企画書 §9.2）があると、同じ `turn` に 3 つ以上のエントリが並ぶことがある。
 * ラウンド数を知りたい場合は最後のエントリの `turn` を見る。
 */
export const battleTurnSchema = z.object({
    turn: z.number().int().positive(),
    /** 行動した側。 */
    actor: battleSideSchema,
    /**
     * この行動で HP が変わる側。攻撃なら `actor` の反対側、回復なら `actor` 自身。
     *
     * 「反対側」を読む側に推論させない（ADR-008）。推論は、それが破れる行動が増えたときに
     * 静かに壊れる。`targetHpAfter` は常にこの側の HP を指す。
     */
    target: battleSideSchema,
    action: battleActionSchema,
    /**
     * 与えたダメージ。着弾すれば最低 1 が保証される（企画書 §31）。
     *
     * **攻撃行動で 0 は回避されたことを意味する**（`docs/specs/battle-log.md`）。
     * `REGENERATE` は常に 0 なので、回避の判別は攻撃行動に限ること。
     */
    damage: z.number().int().min(0),
    /**
     * 回復した HP。`REGENERATE` 以外では 0。
     *
     * `damage` に回復量を入れて `action` で読み分ける形にしなかった理由は ADR-008。
     */
    heal: z.number().int().min(0),
    /** クリティカルか。演出側はこれでヒットストップとカメラシェイクを分岐する。 */
    critical: z.boolean(),
    /** 行動後の、`target` の残 HP。0 で戦闘終了。 */
    targetHpAfter: z.number().int().min(0),
});
/**
 * 戦闘の完全な記録。**演出側との契約であり、その仕様の正**。
 *
 * ---
 *
 * ## この形を変更するときは
 *
 * 演出側（iOS）は Swift なので TypeScript の型を共有できない。**契約がズレても
 * TypeScript 側は何も言わない。**
 *
 * 形を変えるときは次の 3 箇所を必ず同じ PR で更新すること。
 *
 * 1. このスキーマ
 * 2. `packages/shared/src/fixtures/battle-log.sample.json`（ゴールデンフィクスチャ）
 * 3. `docs/specs/battle-log.md`（JSON 契約仕様）
 *
 * 1 と 2 のズレは `battle-log.fixture.test.ts` が CI で捕まえる。3 は人間が守る。
 * iOS 側とのズレは `packages/contracts/` の契約テストが捕まえる。
 *
 * 詳細は `docs/adr/006-godot-battle-log-contract.md`（契約の定義）と
 * `docs/adr/016-battle-rendering-spritekit.md`（演出が Godot から SpriteKit に変わった経緯）。
 *
 * ---
 *
 * ## 再現性
 *
 * `seed` と両者の `BattleCharacter` があれば、`simulateBattle` は必ず同じ `turns` と
 * `winner` を返す（企画書 §10.3）。このログを保存しておけば後からリプレイでき、
 * 結果が正しいことを検証し直せる。
 */
export const battleLogSchema = z.object({
    battleId: z.string().min(1),
    /** この戦闘の乱数シード。同じ入力と同じ seed からは同じ結果が再現される。 */
    seed: z.number().int(),
    characterA: battleCharacterSchema,
    characterB: battleCharacterSchema,
    turns: z.array(battleTurnSchema),
    winner: battleOutcomeSchema,
});
/**
 * バトルシミュレーションのリクエスト。
 *
 * `seed` は省略可能。省略された場合はサーバーが生成し、**生成した値を必ずレスポンスの
 * `BattleLog.seed` に含める**（企画書 §10.3）。含めないと、その戦闘は二度と再現できない。
 */
export const simulateBattleSchema = z.object({
    characterA: battleCharacterSchema,
    characterB: battleCharacterSchema,
    seed: z.number().int().optional(),
});
/**
 * 永続化する対戦のリクエスト。
 *
 * **ステータスを受け取らない。** キャラクターの ID だけを受け、サーバーが D1 から読んだ
 * 値で計算する（企画書 §40-7「クライアントを信用しない」）。ステータスをリクエストで
 * 受けると、任意の強さのキャラクターで戦績を積めてしまう。
 *
 * ステータスを直接渡せるのは保存しない `simulateBattleSchema` の方だけ。**保存しないから
 * 安全**という線引きになっている（ADR-009）。
 */
export const createBattleSchema = z.object({
    characterAId: z.string().min(1),
    characterBId: z.string().min(1),
    /** 省略時はサーバーが生成し、`BattleLog.seed` に載せて返す。 */
    seed: z.number().int().optional(),
});
/**
 * 保存された対戦の要約。
 *
 * `BattleLog` の本体は含まない。一覧や戦績の表示でログを運ばないため（ADR-009）。
 * リプレイには `GET /battles/:id` を使う。
 */
export const battleSummarySchema = z.object({
    id: z.string().min(1),
    characterAId: z.string().min(1),
    characterBId: z.string().min(1),
    winner: battleOutcomeSchema,
    seed: z.number().int(),
    /** 計算に使ったルール定数の版数。保存済みログとの突き合わせに使う。 */
    rulesVersion: z.string().min(1),
    turnCount: z.number().int().nonnegative(),
    roundCount: z.number().int().nonnegative(),
    createdAt: z.string().min(1),
});
/**
 * 保存された対戦。リプレイに必要なものが揃っている。
 *
 * `log` が省略可能なのは、**古いログをアーカイブで落とせる**ようにしてあるため
 * （ADR-009）。ログの無い対戦は「再生できない古い対戦」として扱い、戦績は要約から読む。
 */
export const battleSchema = battleSummarySchema.extend({
    log: battleLogSchema.optional(),
});
/**
 * CPU との対戦（`POST /api/v1/battles/cpu`、ADR-020）。
 *
 * **自分のキャラクターの ID と、CPU の定義の ID だけを受ける。** ステータスはどちらもサーバーが持つ
 * （企画書 §40-7）。CPU 戦は勝敗数（`wins` / `losses`）を更新せず、練習の戦績（`cpuRecord`）だけを増やす。
 */
export const createCpuBattleSchema = z.object({
    characterId: z.string().min(1),
    cpuOpponentId: z.enum(CPU_OPPONENT_IDS),
    /** 省略時はサーバーが生成し、`BattleLog.seed` に載せて返す。 */
    seed: z.number().int().optional(),
});
/** CPU の対戦相手（`GET /api/v1/cpu-opponents`）。値の正は `CPU_OPPONENTS`。 */
export const cpuOpponentSchema = z.object({
    id: z.enum(CPU_OPPONENT_IDS),
    name: z.string().min(1),
    tier: z.enum(CPU_TIERS),
    potentialPoints: z.number().int(),
    archetypeId: archetypeIdSchema,
    elementId: elementIdSchema,
    traitIds: z.array(traitIdSchema),
    stats: characterStatsSchema,
    /** 必殺技の名前（#204）。 */
    specialMoveName: z.string().min(1),
});
